/**
 * Social Analytics filter state → request mapping.
 *
 * The dashboard's page-level filters split into two levels (entities plan,
 * part 6 §1):
 *
 *   - **entity-level** (videos / posts): label filters, platforms, metric
 *     ranges and the published-date range. These travel inside the `entity`
 *     sub-object of every request body, where the server lowers them to a
 *     semi-join over `auto_coder_entities`.
 *   - **record-level** (comments): metadata filters and the comment-date
 *     range. These stay at the top level of `LabelQuery`.
 *
 * The legacy top-level `textFilters` / `rangeFilters` / `publishedDateRange`
 * fields (pipeline labels) are deliberately no longer emitted — the sidebar
 * now sources its keys from the entity filter-summary, so the keys are in the
 * entity domain and only the entity path can resolve them.
 *
 * Everything here is pure so it can be unit-tested without React.
 */

import type { DateRange, EntityQuery, LabelQuery, RangeFilter } from './types'
import {
  authorScopeQuery,
  sourceScopeQuery,
  withoutToolbarOwnedKeys,
  ALL_SOURCES,
  type AuthorScope,
  type SourceScope,
} from './platformFilter'

/**
 * The reserved axis / filter key for the platform dimension. The crosstab
 * engine recognizes `platform(*)` as a first-class top bucket ahead of any
 * label lookup, so selecting 2+ platforms can drive comparison mode.
 */
export const PLATFORM_FILTER_KEY = 'platform'

/** The full page-level filter state, flat and serializable. */
export interface SocialFilterState {
  /** Entity label filters — exact match, OR within values, AND across keys. */
  labelFilters: Record<string, string[]>
  /** Entity platforms (OR within values). Empty = no constraint. */
  platforms: string[]
  /**
   * Business sources (social, influencer, …) — the Source row. Empty means
   * no constraint, exactly as an empty platform list does.
   */
  sources: string[]
  /**
   * Sprinklr campaign ids (OR within values). Empty = no constraint.
   *
   * Deliberately NOT `labelFilters.campaign`: the label carries whatever display
   * name the channel manifest stamped, while this is the real column holding
   * Sprinklr's own id — the thing that matches the manifest and survives a
   * rename. The `none` / `unknown` sentinels are valid selections; "which videos
   * have no campaign" is a question worth being able to ask.
   */
  campaigns: string[]
  /** Numeric ranges over entity *metrics*, keyed by metric key. */
  metricRanges: Record<string, RangeFilter>
  /** Entity kinds (OR within values). Empty = no constraint. */
  kinds: string[]
  /** Entity published-date range. */
  publishedDateRange: DateRange | null
  /** Record-level metadata equality filters. */
  metadataFilters: Record<string, string>
  /** Record-level comment-date range. */
  commentDateRange: DateRange | null
  /**
   * Record-level author scope. `'all'` is the neutral value used by this EMPTY
   * state; the dashboard itself opens on `DEFAULT_AUTHOR_SCOPE` ('audience') —
   * see platformFilter.ts for why the page default is not the empty default.
   */
  authorScope: AuthorScope
  /**
   * Record-level ingest source. `'all'` sends no predicate; a single source is
   * a positive `metadata.source` match, never an exclusion — see
   * platformFilter.ts for why absence is not evidence here.
   */
  sourceScope: SourceScope
}

export const EMPTY_SOCIAL_FILTER_STATE: SocialFilterState = {
  labelFilters: {},
  platforms: [],
  sources: [],
  campaigns: [],
  metricRanges: {},
  kinds: [],
  publishedDateRange: null,
  metadataFilters: {},
  commentDateRange: null,
  authorScope: 'all',
  sourceScope: 'all',
}

/**
 * Build the `entity` sub-object, or `undefined` when no entity-level filter is
 * active (an empty object would be indistinguishable from "no filter" on the
 * wire, but sending nothing keeps the request bodies — and therefore the
 * react-query keys — minimal).
 */
export function buildEntityQuery(state: SocialFilterState): EntityQuery | undefined {
  const entity: EntityQuery = {}

  if (Object.keys(state.labelFilters).length > 0) {
    entity.labelFilters = state.labelFilters
  }
  if (state.platforms.length > 0) {
    entity.platforms = state.platforms
  }
  if (state.sources.length > 0) {
    entity.sources = state.sources
  }
  if (state.campaigns.length > 0) {
    entity.campaigns = state.campaigns
  }
  if (state.kinds.length > 0) {
    entity.kinds = state.kinds
  }
  if (Object.keys(state.metricRanges).length > 0) {
    entity.metricRanges = state.metricRanges
  }
  if (state.publishedDateRange) {
    entity.publishedDateRange = state.publishedDateRange
  }

  return Object.keys(entity).length > 0 ? entity : undefined
}

/**
 * Build the unified `LabelQuery` sent with every widget request.
 * `JSON.stringify` of the result is the page's `labelsKey`.
 */
export function buildLabelQuery(state: SocialFilterState): LabelQuery {
  const query: LabelQuery = {}

  const entity = buildEntityQuery(state)
  if (entity) {
    query.entity = entity
  }

  // The author scope is record-level, so it sits BESIDE `entity`, never inside
  // it: a comment's author is not a property of the video it hangs off.
  const author = authorScopeQuery(state.authorScope)
  const source = sourceScopeQuery(state.sourceScope)
  // `author_type` and `source` belong to the toolbar controls, which are always
  // on screen. A stale (or hand-edited) sidebar filter over the same key would
  // be overwritten by the spread below with no signal at all, so it is dropped
  // explicitly here and never offered by the sidebar in the first place — one
  // key, one control, no silent disagreement.
  const metadataFilters = {
    ...withoutToolbarOwnedKeys(state.metadataFilters),
    ...(author.metadataFilters ?? {}),
    ...(source.metadataFilters ?? {}),
  }
  if (Object.keys(metadataFilters).length > 0) {
    query.metadataFilters = metadataFilters
  }
  if (author.metadataExclusions) {
    query.metadataExclusions = author.metadataExclusions
  }
  // The source control is a MULTI-select and lowers to metadataAny (T3.5), so
  // it rides beside metadataFilters rather than inside it.
  if (source.metadataAny) {
    query.metadataAny = { ...(query.metadataAny ?? {}), ...source.metadataAny }
  }
  if (state.commentDateRange) {
    query.commentDateRange = state.commentDateRange
  }

  return query
}

/** Information about comparison mode state. */
export interface ComparisonModeInfo {
  isComparisonMode: boolean
  comparisonLabelKey: string | null
  comparisonValues: string[]
}

const NO_COMPARISON: ComparisonModeInfo = {
  isComparisonMode: false,
  comparisonLabelKey: null,
  comparisonValues: [],
}

/**
 * Comparison mode is triggered when 2+ values are selected for the same entity
 * label key, or when 2+ platforms are selected ("compare TikTok vs YouTube").
 * An explicit label multi-select wins over platforms.
 */
export function detectComparisonMode(
  state: Pick<SocialFilterState, 'labelFilters' | 'platforms'>,
): ComparisonModeInfo {
  for (const [key, values] of Object.entries(state.labelFilters)) {
    if (values.length >= 2) {
      return { isComparisonMode: true, comparisonLabelKey: key, comparisonValues: values }
    }
  }
  if (state.platforms.length >= 2) {
    return {
      isComparisonMode: true,
      comparisonLabelKey: PLATFORM_FILTER_KEY,
      comparisonValues: state.platforms,
    }
  }
  return NO_COMPARISON
}

export interface CountActiveFiltersOptions {
  /**
   * Whether the SOURCE control is on screen.
   *
   * 🔴 WHY THIS OPTION EXISTS. The badge's job is to tell the user how many
   * filters they have applied and could clear. A scope that is FORCED behind a
   * hidden control is neither: on the admin `all` page the source is pinned to
   * `sprinklr` with no toggle rendered (2026-08-24), and counting it parked a
   * permanent "1 active filter" beside a Clear All button that could not move
   * it — the exact unclearable-filter failure the toolbar's own documentation
   * argues against, reintroduced by the fix meant to honour it.
   *
   * `authorScope` needs no equivalent only because it is forced to `all`, a
   * value the count already scores as zero. `sprinklr` is a real value, so this
   * has to be said out loud.
   *
   * Defaults to `true`: every caller that does not hide the control keeps
   * today's behaviour exactly.
   */
  sourceVisible?: boolean
  /**
   * The date range the page opened on (the Sky dashboards' "up to yesterday").
   * A date filter still equal to it is not a selection the user made, so it
   * scores zero; one they changed, or cleared, scores one.
   */
  defaultDateRange?: DateRange | null
}

/** Two ranges filter the same days. Absent ends compare equal to undefined ones. */
export function isSameDateRange(a: DateRange | null | undefined, b: DateRange | null | undefined): boolean {
  if (!a || !b) return !a && !b
  return (a.from || undefined) === (b.from || undefined) && (a.to || undefined) === (b.to || undefined)
}

/**
 * Number of active filter selections, for the sidebar badge.
 *
 * Counts what the user has CHOSEN and could clear — which is why the toolbar's
 * forced values are excluded (see `sourceVisible`) and why toolbar-owned
 * metadata keys are dropped before counting, so one key cannot score twice.
 */
export function countActiveFilters(
  state: SocialFilterState,
  options: CountActiveFiltersOptions = {},
): number {
  const { sourceVisible = true, defaultDateRange = null } = options
  const labelCount = Object.values(state.labelFilters).reduce((sum, values) => sum + values.length, 0)
  const platformCount = state.platforms.length
  const sourcesCount = state.sources.length
  // 🔴 COUNTED BECAUSE CLEAR ALL CLEARS IT (#1199, 2026-09-07). Campaign was a
  // filter only the admin view could set, and it was scored as zero. With the
  // per-department picker it is an ordinary selection, and a filter the badge
  // reads as zero while Clear All visibly changes every number on the page is
  // the badge/reality disagreement the sourceVisible option below exists to
  // prevent — arriving from the other direction.
  //
  // Empty is the neutral "every campaign" state, so it scores zero: not a
  // selection anyone made, and not one they can be asked to clear.
  const campaignCount = state.campaigns.length
  const kindCount = state.kinds.length
  const rangeCount = Object.keys(state.metricRanges).length
  const dateCount =
    (isSameDateRange(state.publishedDateRange, defaultDateRange) ? 0 : 1) +
    (isSameDateRange(state.commentDateRange, defaultDateRange) ? 0 : 1)
  // Toolbar-owned keys are excluded here for the same reason `buildLabelQuery`
  // drops them: they are not sidebar filters, and counting one twice (once as
  // metadata, once as the source scope) would make the badge disagree with the
  // summary bar.
  const metadataCount = Object.keys(withoutToolbarOwnedKeys(state.metadataFilters)).length
  // A hidden control's scope is not a selection the user made, so it is not one
  // they can be asked to clear.
  const sourceCount = !sourceVisible || state.sourceScope === ALL_SOURCES ? 0 : 1
  return (
    sourceCount +
    labelCount +
    platformCount +
    sourcesCount +
    campaignCount +
    kindCount +
    rangeCount +
    dateCount +
    metadataCount
  )
}
